import flagCivicPapers from "@/assets/flag-civic-papers.svg";
import flagDangerous from "@/assets/flag-dangerous.svg";
import flagGangMembers from "@/assets/flag-gang-members.svg";
import flagInJail from "@/assets/flag-in-jail.svg";
import flagMedical from "@/assets/flag-medical.svg";
import flagNotes from "@/assets/flag-notes.svg";
import flagWarrants from "@/assets/flag-warrants.svg";
import { useRef, useState } from "react";
import styled from "styled-components";
import { FlagContainer } from "./styles";
import { StyledFlex } from "@/common/styles";
import CustomModal from "@/common/elements/Modal";
import Button from "@/common/elements/Button";
import Table from "@/common/elements/Table";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { useDispatch, useSelector } from "react-redux";
import {
  setDeleteAPis,
  setBookings,
  setDeleteAPisList,
  setBookingsFieldsEnabled,
} from "@/slices/bookingsSlice";
import {
  setActiveWarrant,
  setActiveWarrantIndex,
  clearActiveWarrant,
} from "@/slices/warrantsSlice";
import WarrantFullDetails from "@/components/Warrants/WarrantFullDetails";
import API_ENDPOINTS from "@/util/api";
import { Modal, Tag } from "antd";
import { getOptionsFromRefList } from "@/util/common_function";
import {
  getAllFlagTableRows,
  getDisplayInvolvements,
  getQuestionnaireInvolvements,
  getFlagSubTypeLabel,
  getFlagSubTypeFieldInfo,
  getFlagDeleteBlockedMessage,
  getInvolvementFlagTypeConstant,
  FLAG_TYPE_LABELS,
} from "@/components/BookingsDetails/JacketDetails/flagUtils";

export const INMATE_FLAG_TYPE = {
  WARRANT: "FLAGTYPES_WARRANTS",
  CIVIC: "FLAGTYPES_DOCUMENTS",
  DANGER: "FLAGTYPES_DANGER",
  DOCUMENTS: "FLAGTYPES_DOCUMENTS",
  INJAIL: "FLAGTYPES_INJAIL",
  GANG: "FLAGTYPES_GANG",
  MEDICAL: "FLAGTYPES_MEDICAL",
  NOTES: "FLAGTYPES_NOTES",
};

export const getFlagIcon = (type) => {
  switch (type) {
    case INMATE_FLAG_TYPE.WARRANT:
      return flagWarrants;
    case INMATE_FLAG_TYPE.CIVIC:
      return flagCivicPapers;
    case INMATE_FLAG_TYPE.DANGER:
      return flagDangerous;
    case INMATE_FLAG_TYPE.INJAIL:
      return flagInJail;
    case INMATE_FLAG_TYPE.GANG:
      return flagGangMembers;
    case INMATE_FLAG_TYPE.MEDICAL:
      return flagMedical;
    case INMATE_FLAG_TYPE.NOTES:
      return flagNotes;
    default:
      return null;
  }
};

export const getFlagName = (type) => {
  switch (type) {
    case INMATE_FLAG_TYPE.WARRANT:
      return "Warrants";
    case INMATE_FLAG_TYPE.CIVIC:
      return "Civil";
    case INMATE_FLAG_TYPE.DANGER:
      return "Danger";
    case INMATE_FLAG_TYPE.INJAIL:
      return "In Jail";
    case INMATE_FLAG_TYPE.GANG:
      return "Gang";
    case INMATE_FLAG_TYPE.MEDICAL:
      return "Medical";
    case INMATE_FLAG_TYPE.NOTES:
      return "Notes";
    default:
      return "";
  }
};

// The Flags dialog's visible columns. getAllFlagTableRows still returns the
// full ASP columnsFlagInstancesFullList row shape (expirationDate, objId,
// linkedObjectType, flagTypeId, invId, relatedObjectId, relatedObjectType) -
// those fields stay on each row for selection/delete/details logic below,
// just not rendered as their own columns here.
const flagListHeaders = [
  { key: "flag", text: "Flag" },
  { key: "summaryDescription", text: "Summary Description" },
  { key: "relatedRecordSummary", text: "Related Record Summary" },
  { key: "primaryObjectFieldSource", text: "PrimaryObjectFieldSource" },
];

export const subTypeLabels = {
  FLAGTYPES_DANGER: "Danger Type",
  FLAGTYPES_GANG: "Gang Type",
  FLAGTYPES_MEDICAL: "Medical Type",
  FLAGTYPES_NOTES: "Note Type",
};

// Deep-clones an inmateClassificationQuestionnaires/medicalQuestionnaires
// array along with each item's own cdInvolvement array, so a snapshot taken
// before the Flags dialog opens isn't mutated by edits made while it's open.
const cloneWithCdInvolvement = (list) =>
  (list || []).map((c) => ({
    ...c,
    cdInvolvement: [...getQuestionnaireInvolvements(c)],
  }));

// Strips a deleted flag's invId out of every item's nested cdInvolvement in
// an inmateClassificationQuestionnaires/medicalQuestionnaires array.
const removeInvIdFromCdInvolvement = (list, isTarget) =>
  (list || []).map((c) => ({
    ...c,
    cdInvolvement: getQuestionnaireInvolvements(c).filter(
      (item) => !isTarget(item),
    ),
  }));

// TextField's disabled textarea (FormControl.TextArea) doesn't pick up
// FormControl's own disabled-text-color rule - styled(Input).TextArea is
// just a hoisted reference to antd's raw Input.TextArea, not a styled one -
// so it falls back to antd's default disabled color, which reads as
// invisible against the light disabled background. Force it readable here.
export const ReadableDisabledTextArea = styled.div`
  textarea.ant-input-disabled,
  textarea[disabled] {
    color: var(--input-text-disabled, #838383) !important;
    -webkit-text-fill-color: var(--input-text-disabled, #838383) !important;
  }
`;

const InmateFlag = ({
  type = INMATE_FLAG_TYPE.WARRANT,
  flagEnabled = false,
  isSkew = true,
}) => {
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [selectedInvId, setSelectedInvId] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [detailItemType, setDetailItemType] = useState(null);
  const [showWarrantDetail, setShowWarrantDetail] = useState(false);
  const dispatch = useDispatch();
  const { bookings, bookingsFieldsEnabled, deleteAPis, jacketFlags } =
    useSelector((state) => state?.bookings);

  // The Flags dialog always lists every active flag on this booking (all
  // types together), matching the ASP "Flags -- Webpage Dialog" - not just
  // flags matching whichever icon was clicked. Includes flags nested inside
  // each Classification questionnaire's own cdInvolvement, not just the
  // booking-root cdInvolvements array - see getAllInvolvements. Saved flags
  // are limited to what flags/jacket/{jacketId} returned - see
  // getDisplayInvolvements.
  const allInvolvements = getDisplayInvolvements(bookings, jacketFlags);
  const flagRows = getAllFlagTableRows(allInvolvements, bookings);
  // Resolved through the row (not invId) so an unsaved flag - no invId yet -
  // can still be selected for Details/Delete.
  const selectedItem = flagRows.find((row) => row.id === selectedInvId)?.source;

  // Snapshot cdInvolvements (and how many deletes were already queued) the
  // moment this Flags dialog opens, so its own Cancel button can undo any
  // flag deletions made while it was open - matching PCI's "OK / Cancel"
  // footer on the "Flags -- Webpage Dialog".
  const snapshotRef = useRef(null);

  // WarrantFullDetails has no "read only" prop of its own - every sub-form
  // (fields, and the Charges Add/Details/Delete buttons) keys off the
  // global bookingsFieldsEnabled flag. This "Details" drill-down is meant
  // to be a strictly read-only peek, so force it false while open and
  // restore whatever it actually was on close.
  const bookingsFieldsEnabledRef = useRef(bookingsFieldsEnabled);

  const onFlagClick = () => {
    snapshotRef.current = {
      cdInvolvements: bookings?.cdInvolvements
        ? [...bookings.cdInvolvements]
        : [],
      // Also snapshot each Classification/Medical questionnaire's own
      // cdInvolvement array (deleting a nested flag from the dialog below
      // mutates it too), so Cancel can restore those alongside the root
      // array.
      inmateClassificationQuestionnaires: cloneWithCdInvolvement(
        bookings?.inmateClassificationQuestionnaires,
      ),
      medicalQuestionnaires: cloneWithCdInvolvement(
        bookings?.medicalQuestionnaires,
      ),
      deleteApisCount: deleteAPis?.length || 0,
    };
    setSelectedInvId(null);
    setDetailsVisible(true);
  };

  // OK: keep whatever was staged (deleted flags stay out of the payload).
  const onFlagModalOk = () => {
    setDetailsVisible(false);
    setSelectedInvId(null);
  };

  // Cancel (button, X, or backdrop): restore cdInvolvements to how it was
  // when the dialog opened and drop any deletes queued since then.
  const onFlagModalHide = () => {
    const snapshot = snapshotRef.current;
    if (snapshot) {
      dispatch(
        setBookings({
          cdInvolvements: snapshot.cdInvolvements,
          inmateClassificationQuestionnaires:
            snapshot.inmateClassificationQuestionnaires,
          medicalQuestionnaires: snapshot.medicalQuestionnaires,
        }),
      );
      if ((deleteAPis?.length || 0) > snapshot.deleteApisCount) {
        dispatch(
          setDeleteAPisList(deleteAPis.slice(0, snapshot.deleteApisCount)),
        );
      }
    }
    setDetailsVisible(false);
    setSelectedInvId(null);
  };

  const onDetailsButtonClick = () => {
    if (!selectedItem) return;
    const itemType = getInvolvementFlagTypeConstant(selectedItem);

    // Matches PCI: a Warrants flag's "Details" opens the actual warrant
    // record (State Submission / Warrant / Offense / Court Information),
    // not the generic Type + Description form the other flag types use.
    if (itemType === INMATE_FLAG_TYPE.WARRANT) {
      const relatedType = selectedItem.relatedObjectType;
      const relatedId =
        selectedItem.relatedObjectID ?? selectedItem.relatedObjectId;
      if (relatedType === "Warrants") {
        const warrants = bookings?.warrants || [];
        const warrantIndex = warrants.findIndex(
          (w) => String(w.warrantId ?? w.warrantID) === String(relatedId),
        );
        if (warrantIndex >= 0) {
          bookingsFieldsEnabledRef.current = bookingsFieldsEnabled;
          dispatch(setBookingsFieldsEnabled(false));
          dispatch(setActiveWarrant(warrants[warrantIndex]));
          dispatch(setActiveWarrantIndex(warrantIndex));
          setShowWarrantDetail(true);
          return;
        }
      }
    }

    setDetailItem(selectedItem);
    setDetailItemType(itemType);
  };

  const onDetailModalHide = () => {
    setDetailItem(null);
    setDetailItemType(null);
  };

  const onWarrantDetailHide = () => {
    setShowWarrantDetail(false);
    dispatch(clearActiveWarrant());
    dispatch(setBookingsFieldsEnabled(bookingsFieldsEnabledRef.current));
  };

  const handleDeleteClick = (item) => {
    const itemType = getInvolvementFlagTypeConstant(item);

    // Matches PCI: Warrant / Civil Paper / In Jail flags aren't
    // user-deletable - see getFlagDeleteBlockedMessage. In Jail is cleared
    // automatically on release (see removeInJailFlag), never by hand.
    const blockedMessage = getFlagDeleteBlockedMessage(itemType);
    if (blockedMessage) {
      Modal.warning({
        title: "IMATS",
        content: blockedMessage,
        okText: "OK",
      });
      return;
    }

    const typeName = FLAG_TYPE_LABELS[itemType] || "";
    const addInfo = getFlagSubTypeLabel(itemType, item);

    Modal.confirm({
      title: "IMATS",
      content: (
        <div style={{ marginTop: 10 }}>
          <p style={{ marginBottom: 16 }}>
            Are you sure you want to delete this object?
          </p>

          <div style={{ lineHeight: "24px" }}>
            <div>
              <b>Flag:</b> {typeName}
            </div>
            <div>
              <b>Summary Description:</b> {addInfo}. {item.invDesc}
            </div>
            <div>
              <b>ID:</b> {item.invObjId}
            </div>
            <div>
              <b>LinkedObjectType:</b> {item.linkedObjectType}
            </div>
          </div>
        </div>
      ),
      okText: "Yes",
      cancelText: "No",
      onOk: () => {
        deleteInvolvement(item);
        setSelectedInvId(null);
        onDetailModalHide();

        // Matches PCI: a second, info-only popup confirms the delete is
        // staged - it isn't final until the record (Jacket Details) is
        // saved, and clicking Cancel there restores the flag.
        Modal.info({
          title: "IMATS",
          content:
            "When you save this record, this flag will be deleted. If you change your mind about deleting it, click Cancel and it will not be deleted.",
          okText: "OK",
        });
      },
    });
  };

  const deleteInvolvement = (target) => {
    const invId = target?.invId;
    // A saved flag is matched (and DELETEd) by invId; an unsaved one has no
    // invId and nothing to DELETE, so it's matched by reference instead -
    // filtering on an undefined invId used to drop every unsaved flag.
    const isTarget = (item) =>
      invId != null ? item.invId === invId : item === target;

    if (invId != null) {
      dispatch(setDeleteAPis(API_ENDPOINTS.jacketFlags + invId));
    }

    // The flag being deleted may live at the booking root (most flag
    // sources) or nested inside a Classification/Medical questionnaire's own
    // cdInvolvement (see getAllInvolvements) - strip it from all of them;
    // whichever one doesn't actually have it is a no-op.
    dispatch(
      setBookings({
        cdInvolvements: (bookings?.cdInvolvements || []).filter(
          (item) => !isTarget(item),
        ),
        inmateClassificationQuestionnaires: removeInvIdFromCdInvolvement(
          bookings?.inmateClassificationQuestionnaires,
          isTarget,
        ),
        medicalQuestionnaires: removeInvIdFromCdInvolvement(
          bookings?.medicalQuestionnaires,
          isTarget,
        ),
      }),
    );
  };

  const detailSubTypeFieldInfo = getFlagSubTypeFieldInfo(detailItemType);
  const detailSubTypeOptions = detailSubTypeFieldInfo
    ? getOptionsFromRefList(
        detailSubTypeFieldInfo.refListName,
        false,
        false,
        true,
      )
    : [];

  return (
    <>
      <FlagContainer id={type} key={type} skew={isSkew}>
        <button
          type="button"
          className={`flag-btn ${getFlagName(type)
            ?.toLowerCase()
            ?.split(" ")
            ?.join("-")} ${flagEnabled ? "enabled" : ""}`}
          onClick={onFlagClick}
        >
          <div className="flag">
            <img
              alt={`flag ${type} icon`}
              src={getFlagIcon(type)}
              className="flag-main-icon"
            />
            <div className="flag-info">
              <span className="flag-name">{getFlagName(type)}</span>
            </div>
          </div>
        </button>
      </FlagContainer>

      {detailsVisible && (
        <CustomModal
          show={detailsVisible}
          onHide={onFlagModalHide}
          header="Flags"
          size="lg"
          footerActions={[
            { key: "cancel", text: "Cancel", onClick: onFlagModalHide },
            { key: "ok", text: "OK", onClick: onFlagModalOk },
          ]}
        >
          <div>
            <Tag key={"orange"} color={"orange"} variant={"solid"}>
              Flags that have not expired or been deleted
            </Tag>
          </div>

          <StyledFlex
            gap="10px"
            style={{ marginBottom: "10px", marginTop: "10px" }}
          >
            <Button disabled={!selectedItem} onClick={onDetailsButtonClick}>
              Details
            </Button>
            <Button
              disabled={!selectedItem || !bookingsFieldsEnabled}
              onClick={() => selectedItem && handleDeleteClick(selectedItem)}
            >
              Delete
            </Button>
          </StyledFlex>
          <Table
            headers={flagListHeaders}
            data={flagRows}
            onRowClick={(rowData) => setSelectedInvId(rowData.id)}
            noDataMessage="No active flags."
          />
        </CustomModal>
      )}

      {detailItem && (
        <CustomModal
          show={!!detailItem}
          onHide={onDetailModalHide}
          header={`${FLAG_TYPE_LABELS[detailItemType] || ""} Flag`}
          size="md"
          footerActions={[
            { key: "close", text: "Close", onClick: onDetailModalHide },
          ]}
        >
          {detailSubTypeFieldInfo && (
            <div style={{ marginBottom: "15px" }}>
              <label>{subTypeLabels[detailItemType] || "Type"}</label>
              <Select
                options={detailSubTypeOptions}
                showSearch
                value={detailItem[detailSubTypeFieldInfo.fieldName] ?? null}
                disabled
              />
            </div>
          )}

          <div style={{ marginBottom: "15px" }}>
            <label>Description</label>
            <ReadableDisabledTextArea>
              <TextField
                id="flagDetailsDescription"
                as="textarea"
                disabled
                value={detailItem.invDesc || ""}
                rows={5}
              />
            </ReadableDisabledTextArea>
          </div>

          {detailItem?.invExpireDate && (
            <div style={{ marginTop: "10px" }}>
              <b>Expire Date:</b> {detailItem.invExpireDate}
            </div>
          )}
        </CustomModal>
      )}

      {showWarrantDetail && (
        <CustomModal
          show={showWarrantDetail}
          onHide={onWarrantDetailHide}
          header="Warrant Details"
          size="xl"
          footerActions={[
            { key: "close", text: "Close", onClick: onWarrantDetailHide },
          ]}
        >
          <WarrantFullDetails mode="edit" onBack={onWarrantDetailHide} />
        </CustomModal>
      )}
    </>
  );
};

export default InmateFlag;
